import React, { FC, useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Slider, Stack, Typography } from '@mui/material';
import { ControlCamera } from '@mui/icons-material';

import { LiveArea } from './LiveArea';


interface Props {
  imageUrl?: string;
  onImageCropAreaPixelsChange: (data: Area) => void;
  onImageZoomChange?: (zoom: number) => void;
  onImageCropChange?: (crop: { x: number; y: number }) => void
  defaultValue?: CVT.MaybeNull<Tributes.ImageCropData>;
  petName?: string;
}
const defaultCrop: { x: number; y: number } = { x: 0, y: 0 };
const defaultZoom = 1;

const defaultCss: React.CSSProperties = {
  borderWidth: '3px',
  borderStyle: 'solid',
  borderColor: '#b4b5b8',
  borderRadius: '12px',
};

export const CardImageCropper: FC<Props> = (props) => {
  const { imageUrl, onImageCropAreaPixelsChange, onImageZoomChange, onImageCropChange, defaultValue, petName } = props;

  const [crop, setCrop] = useState(defaultValue?.crop || defaultCrop);
  const [zoom, setZoom] = useState(defaultValue?.zoom ?? defaultZoom);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
    onImageCropChange?.(crop);
  }, [onImageCropChange]);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
    onImageZoomChange?.(zoom);
  }, [onImageZoomChange]);

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    onImageCropAreaPixelsChange(croppedAreaPixels);
  }, [onImageCropAreaPixelsChange]);

  return (
    <Stack>
      <Stack
        position="relative"
        sx={{
          aspectRatio: '0.63',
          cursor: 'move',
        }}
      >
        <Cropper
          image={imageUrl || ''}
          crop={crop}
          zoom={zoom}
          aspect={0.63}

          cropShape="rect"
          showGrid={false}
          onCropChange={onCropChange}
          onCropComplete={onCropComplete}
          onZoomChange={onZoomChange}
          objectFit="vertical-cover"
          style={{
            containerStyle: {
              ...defaultCss,
              cursor: 'move',
            },
          }}
        />
        <LiveArea petName={petName} defaultCss={defaultCss} />
      </Stack>

      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        mt={2}
        gap={2}
      >
        <Slider
          value={zoom}
          min={1}
          max={4}
          step={0.1}
          aria-labelledby="Zoom"
          onChange={(e, zoom) => onZoomChange(zoom)}
        />
        <Typography variant="caption">Zoom</Typography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        mt={2}
        gap={2}
      >
        <ControlCamera/>
        <Typography variant="body2" color="text.secondary">
          Drag to crop the image
        </Typography>
      </Stack>
    </Stack>
  );
};
